// Player join/update command flow and acknowledgement handling.

import React from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  createLogger,
  generateCommandId,
} from '@airhead-games/shared/utilities';
import { getRoomCapacityRemaining } from '@airhead-games/shared/engine/roomState';
import {
  PlayerUpdateType,
  RoomPlayerActionType,
  RoomPlayerCommand,
  SenderRoleType,
} from '@airhead-games/shared/types';

import { updatePlayer } from '../transport/api/publicApi';
import { analyticsService } from '../services/analyticsService';
import { InputValidationError } from './useValidatedInput';
import { useRoomConfig } from './useRoomConfig';
import { useRoomMyPlayer, useRoomSetMyPlayer } from './useRoomMyPlayer';
import { useRoomPlayerLookup } from './useRoomPlayerLookup';
import { useRoomPlayers } from './useRoomPlayers';
import { useRoomStateSelector } from './useRoomStateSelector';
import { useRoomSocket } from './useRoomSocket';
import { useRoomCommandAck } from './useRoomCommandAck';

const logger = createLogger('usePlayerJoinFlow');

interface UsePlayerJoinFlowArgs {
  roomCode: string | undefined;
  selectedColor: string;
  customFace: { eyeShape: string; mouthShape: string };
}

interface UsePlayerJoinFlowResult {
  submit: (playerName: string) => Promise<void | false>;
  isJoining: boolean;
}

export function usePlayerJoinFlow({
  roomCode,
  selectedColor,
  customFace,
}: UsePlayerJoinFlowArgs): UsePlayerJoinFlowResult {
  const navigate = useNavigate();
  const [isJoining, setIsJoining] = React.useState(false);
  const [currentCommandId, setCurrentCommandId] = React.useState<string | null>(
    null
  );
  const {
    status,
    twitchPlayersOnly,
    disallowBlockedAndBanned,
    blockedTwitchUserIds,
  } = useRoomConfig();
  const hasRoomCapacity = useRoomStateSelector((state) =>
    state ? getRoomCapacityRemaining(state) > 0 : true
  );
  const { sendCommand } = useRoomSocket();
  const myPlayer = useRoomMyPlayer();
  const setMyPlayer = useRoomSetMyPlayer();
  const { players } = useRoomPlayers();
  const { getPlayer } = useRoomPlayerLookup();
  const myPlayerInRoom = myPlayer?.playerId
    ? getPlayer(myPlayer.playerId) !== undefined
    : false;

  const goToGame = React.useCallback(() => {
    if (roomCode) {
      navigate(`/game/${roomCode}/playing`);
    }
  }, [navigate, roomCode]);

  useRoomCommandAck({
    commandId: currentCommandId || '',
    onSuccess: () => {
      logger.debug('Join/Update command acknowledged successfully');
    },
    onError: (error) => {
      logger.error('Join/Update command failed:', error);
      const message =
        typeof error === 'string' && error.length > 0
          ? error
          : 'Failed to join room. Please try again.';
      toast.error(message);
      setIsJoining(false);
      setCurrentCommandId(null);
    },
    onTimeout: () => {
      logger.warn('Join/Update command timed out');
      toast.error('Failed to join room. Please try again.');
      setIsJoining(false);
      setCurrentCommandId(null);
    },
  });

  React.useEffect(() => {
    if (!isJoining || !myPlayerInRoom) return;
    goToGame();
  }, [goToGame, isJoining, myPlayerInRoom]);

  const submit = React.useCallback(
    async (playerName: string): Promise<void | false> => {
      analyticsService.trackFormSubmit('confirm_player', 'player_join', {
        room_code: roomCode || 'unknown',
        player_color: selectedColor,
        eye_expression: customFace.eyeShape,
        mouth_expression: customFace.mouthShape,
      });

      if (status === 'playing') {
        toast.error('Game is already in progress.');
        return false;
      }
      if (!hasRoomCapacity) {
        toast.error('Room is full.');
        return false;
      }
      if (!roomCode) {
        logger.error('No room code available for submission.');
        toast.error(`Can't join room. Refresh and try again.`);
        return false;
      }
      if (!myPlayer) {
        logger.error('No player available for submission.');
        toast.error(`Can't join room. Refresh and try again.`);
        return false;
      }
      if (
        twitchPlayersOnly &&
        disallowBlockedAndBanned &&
        myPlayer.twitchUserId &&
        Array.isArray(blockedTwitchUserIds) &&
        blockedTwitchUserIds.includes(myPlayer.twitchUserId)
      ) {
        toast.error('You are not allowed to join this room');
        return false;
      }

      const existingPlayer = players.find(
        (player) =>
          player.playerName.toLowerCase() === playerName.toLowerCase() &&
          player.playerId !== myPlayer.playerId
      );
      if (existingPlayer) {
        throw new InputValidationError(
          'Player name is already taken in this room'
        );
      }

      setIsJoining(true);

      try {
        const updatedPlayer = {
          ...myPlayer,
          playerName,
          playerColor: selectedColor,
          face: customFace,
        };

        logger.info('About to update player in database', {
          playerId: myPlayer.playerId,
          updates: { playerName, playerColor: selectedColor, face: customFace },
          currentPlayer: myPlayer,
        });

        const updateResponse = await updatePlayer(myPlayer.playerId, {
          playerName,
          playerColor: selectedColor,
          face: customFace,
          roomCode,
        });

        logger.info('Successfully updated player in database', {
          playerId: myPlayer.playerId,
          responseData: updateResponse.data,
        });

        setMyPlayer(updatedPlayer);

        const actionType = myPlayerInRoom
          ? RoomPlayerActionType.UPDATE
          : RoomPlayerActionType.JOIN;

        let playerUpdateType: PlayerUpdateType | undefined;
        if (actionType === RoomPlayerActionType.UPDATE) {
          const currentMyPlayer = getPlayer(myPlayer.playerId);
          if (!currentMyPlayer) {
            throw new Error('Cannot update player: player not found in room');
          }

          const nameChanged = currentMyPlayer.playerName !== playerName;
          const colorChanged = currentMyPlayer.playerColor !== selectedColor;

          if (nameChanged && colorChanged) {
            playerUpdateType = PlayerUpdateType.NAME_AND_COLOR;
          } else if (nameChanged) {
            playerUpdateType = PlayerUpdateType.NAME;
          } else if (colorChanged) {
            playerUpdateType = PlayerUpdateType.COLOR;
          }
        }

        const commandId = generateCommandId();
        const command: RoomPlayerCommand = {
          id: commandId,
          type: 'RoomPlayerAction',
          action: actionType,
          senderRole: SenderRoleType.PLAYER,
          clientTimestamp: Date.now(),
          roomCode,
          player: updatedPlayer,
          ...(playerUpdateType && { playerUpdateType }),
        };

        sendCommand(command);
        setCurrentCommandId(commandId);
      } catch (error) {
        logger.error('Failed to update or join player', error);
        toast.error('An unexpected error occurred. Please try again.');
        setIsJoining(false);
        return false;
      }
    },
    [
      blockedTwitchUserIds,
      customFace,
      disallowBlockedAndBanned,
      getPlayer,
      hasRoomCapacity,
      myPlayer,
      myPlayerInRoom,
      players,
      roomCode,
      selectedColor,
      sendCommand,
      setMyPlayer,
      status,
      twitchPlayersOnly,
    ]
  );

  return { submit, isJoining };
}
